import { useEffect, useId, useMemo, useState, type FC, type ReactNode } from "react";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Label } from "@/shared/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { AddPersonForm } from "@/shared/components/AddPersonForm";
import { ContactAvatar } from "@/shared/components/ContactAvatar";
import { useAdminSimulation } from "@/shared/hooks/useAdminSimulation";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { cn } from "@/shared/lib/utils";
import {
  formatContactName,
  formatContactSubtitle,
  getVisibleContacts,
  matchesContactQuery,
} from "@/shared/utils/contactUtils";

import type { PersonContact } from "@/shared/types/estateContact";

interface PeopleSelectorBaseProps {
  label?: string;
  placeholder?: string;
  showSearch?: boolean;
  // Prefills "Add person" when the search is empty, e.g. a name typed before the selector existed
  newPersonName?: string;
  filter?: (contact: PersonContact) => boolean;
  // shortcut: an explicit list hides "Add person", which only creates contacts in the user's active estate
  contacts?: PersonContact[];
  disabled?: boolean;
  error?: string;
  className?: string;
}

type PeopleSelectorProps = PeopleSelectorBaseProps &
  (
    | { mode: "single"; value: number | null; onChange: (value: number | null) => void }
    | { mode: "multi"; value: number[]; onChange: (value: number[]) => void }
  );

const EmptyMessage: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
    <p className="text-sm font-medium">{title}</p>
    <div className="text-xs text-secondary">{children}</div>
  </div>
);

export const PeopleSelector: FC<PeopleSelectorProps> = (props) => {
  const {
    label,
    placeholder = "Choose a person",
    showSearch = true,
    newPersonName = "",
    filter,
    contacts: contactsOverride,
    disabled,
    error,
    className,
  } = props;
  const triggerId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [addingName, setAddingName] = useState<string | null>(null);
  const [addedId, setAddedId] = useState<number | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const { authUser } = useAppContext();
  const { contacts: userContacts, isLoading: isLoadingUser, accountOwner } = useUserContacts();
  const contacts = contactsOverride ?? userContacts;
  const isLoading = !contactsOverride && isLoadingUser;
  const canAdd = !contactsOverride;
  const { isSimulating } = useAdminSimulation();

  const visiblePeople = useMemo(
    () =>
      getVisibleContacts(contacts || [], {
        currentUserEmail: authUser?.email,
        accountOwnerEmail: accountOwner?.email,
        excludeEmail: isSimulating ? authUser?.email : undefined,
      }),
    [contacts, authUser?.email, accountOwner?.email, isSimulating],
  );
  const people = useMemo(
    // Someone added from this field stays pickable here even if the filter would hide them
    () =>
      filter
        ? visiblePeople.filter((contact) => contact.id === addedId || filter(contact))
        : visiblePeople,
    [visiblePeople, filter, addedId],
  );

  const { mode, value, onChange } = props;
  useEffect(() => {
    if (isLoading || !contacts) return;
    const available = new Set(people.map((contact) => contact.id));
    if (mode === "multi") {
      const kept = value.filter((id) => available.has(id));
      if (kept.length !== value.length) onChange(kept);
    } else if (value !== null && !available.has(value)) {
      onChange(null);
    }
  }, [isLoading, contacts, people, mode, value, onChange]);

  // Selects a just-added person once the refetched list reaches the parent, so its onChange sees them
  useEffect(() => {
    if (pendingId === null || !people.some((contact) => contact.id === pendingId)) return;
    setPendingId(null);
    if (mode === "multi") {
      onChange([...value, pendingId]);
    } else {
      onChange(pendingId);
      setOpen(false);
    }
  }, [pendingId, people, mode, value, onChange]);

  const results = people.filter((contact) => matchesContactQuery(contact, query));
  const selectedIds =
    props.mode === "multi" ? props.value : props.value === null ? [] : [props.value];
  const selected = people.filter((contact) => selectedIds.includes(contact.id));

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setAddingName(null);
    }
  };

  const handleSelect = (contactId: number) => {
    if (props.mode === "multi") {
      props.onChange(
        props.value.includes(contactId)
          ? props.value.filter((id) => id !== contactId)
          : [...props.value, contactId],
      );
    } else {
      props.onChange(contactId);
      handleOpenChange(false);
    }
  };

  const fieldValue =
    selected.length === 0 ? (
      <span className="truncate text-tertiary">{placeholder}</span>
    ) : props.mode === "single" ? (
      <span className="flex min-w-0 items-center gap-2">
        <ContactAvatar
          contact={selected[0]}
          className="size-6 shrink-0 bg-background text-xs font-medium text-tertiary"
        />
        <span className="truncate">
          {formatContactName(selected[0].first_name, selected[0].last_name)}
        </span>
      </span>
    ) : (
      <>
        <span className="flex-1 truncate">
          {selected.map((contact) => contact.first_name || contact.last_name).join(", ")}
        </span>
        <span className="rounded-full bg-background px-2 text-xs">{selected.length}</span>
      </>
    );

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && <Label htmlFor={triggerId}>{label}</Label>}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id={triggerId}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-start px-3 text-left font-normal hover:bg-white",
              "focus-visible:border-brand focus-visible:ring-0 data-[state=open]:border-brand",
              error && "border-destructive",
            )}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">{fieldValue}</span>
            <ChevronDown
              className={cn("size-3 shrink-0 transition-transform", open && "rotate-180")}
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] rounded-md p-0"
          onWheel={(event) => event.stopPropagation()}
        >
          {addingName !== null ? (
            <AddPersonForm
              initialName={addingName}
              onCancel={() => setAddingName(null)}
              onAdded={(contactId) => {
                setAddedId(contactId);
                setPendingId(contactId);
                setQuery("");
                setAddingName(null);
              }}
            />
          ) : (
            <Command shouldFilter={false}>
              {showSearch && (
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Search people"
                  className="h-auto py-2 placeholder:text-tertiary"
                  icon={
                    <Search className="mr-2 size-4 shrink-0 text-tertiary" aria-hidden="true" />
                  }
                  action={
                    query && (
                      <button
                        type="button"
                        aria-label="Clear search"
                        className="mr-3 text-tertiary"
                        onClick={() => setQuery("")}
                      >
                        <X className="size-4" />
                      </button>
                    )
                  }
                />
              )}
              <CommandList>
                {isLoading ? (
                  <p className="px-4 py-6 text-center text-sm text-secondary">Loading people...</p>
                ) : visiblePeople.length === 0 ? (
                  <EmptyMessage title="No people added yet">
                    {canAdd
                      ? "Add family and advisors below."
                      : "Add family and advisors in People."}
                  </EmptyMessage>
                ) : people.length === 0 ? (
                  <EmptyMessage title="No eligible people for this field">
                    {canAdd ? "Add them below." : "Add them in People."}
                  </EmptyMessage>
                ) : results.length === 0 ? (
                  <EmptyMessage title={`No one named “${query.trim()}”`}>
                    {canAdd ? "Check the spelling, or add them below." : "Check the spelling."}
                  </EmptyMessage>
                ) : (
                  <CommandGroup>
                    {results.map((contact) => {
                      const isSelected = selectedIds.includes(contact.id);
                      const subtitle = formatContactSubtitle(contact);
                      return (
                        <CommandItem
                          key={contact.id}
                          value={String(contact.id)}
                          onSelect={() => handleSelect(contact.id)}
                          className="gap-3 rounded px-3 py-1.5 hover:bg-subtle data-[selected=true]:bg-subtle"
                        >
                          {props.mode === "multi" && (
                            <span
                              aria-hidden="true"
                              className={cn(
                                "flex size-5 shrink-0 items-center justify-center rounded border",
                                isSelected ? "border-brand bg-brand text-white" : "border-primary",
                              )}
                            >
                              {isSelected && <Check className="size-3.5" strokeWidth={3} />}
                            </span>
                          )}
                          {props.mode === "single" && (
                            <ContactAvatar
                              contact={contact}
                              className="size-7 shrink-0 bg-background text-xs font-medium text-tertiary"
                            />
                          )}
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-medium">
                              {formatContactName(contact.first_name, contact.last_name)}
                            </span>
                            {subtitle && (
                              <span className="truncate text-xs text-secondary">{subtitle}</span>
                            )}
                            {isSelected && <span className="sr-only">Selected</span>}
                          </span>
                          {props.mode === "single" && isSelected && (
                            <Check className="size-4 shrink-0 text-brand" aria-hidden="true" />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
              {canAdd && (
                <button
                  type="button"
                  className="flex w-full items-center gap-3 border-t border-primary px-3 py-2 text-left font-medium text-brand hover:bg-subtle"
                  onClick={() => setAddingName(query.trim() || newPersonName)}
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-dashed border-strong">
                    <Plus className="size-4" aria-hidden="true" />
                  </span>
                  {query.trim() && results.length === 0
                    ? `Add “${query.trim()}” as a new person`
                    : "Add person"}
                </button>
              )}
              {props.mode === "multi" && (
                <div className="flex items-center gap-2 border-t border-primary px-3 py-2">
                  <span className="flex-1 text-xs text-tertiary">{selected.length} selected</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="brand"
                    onClick={() => handleOpenChange(false)}
                  >
                    Done
                  </Button>
                </div>
              )}
            </Command>
          )}
        </PopoverContent>
      </Popover>
      <FieldError message={error} />
    </div>
  );
};
